// Section 25 M1 and Section 9: the founding crew newGame assigns (P-01, superseding D-23 and
// D-28). Nobody who can work starts at rest while a useful post is open (Section 8 rest row:
// idle adults lose happiness and trust). The best healer and toolmaker are kept out of the
// common work unless nobody else can do it; then, in priority order: farmers for the sown plots
// (plotsPerFarmer each, ADR-0020); water carriers until the first year's water is covered with
// a margin; woodcutters until the year's firewood is; the healer and the toolmaker; caretakers
// when there are small children; builders, so the first building the player queues progresses
// from the first season (Section 9: "the first seasons are about deciding what to build
// first"); foragers (diminishing returns) and hunters (a capped share: hunting kills); in a
// large settlement, guards for the safety need. Retirees rest (Section 18: not auto-assigned)
// unless they hold the skill for a post an elder may volunteer for (Section 15: healer,
// teacher, caretaker). Children never start at work (Section 18 child labour: emergencies).
//
// Output and demand come from the modifier pipeline (24.5: `num` on the `output` and `demand`
// targets) in each season of the coming year, so the crew is sized with the numbers the phases
// use. Deterministic: ties go to the least specialised, then the strongest, then id order.
// Constants: config.newGame.crew. `crewPlan: 'm1'` keeps the M1 default (D-23, D-28) for the M2a
// rationing fixture, whose documented shares depend on that crew (M7-14).
import type { Config } from '../config';
import { isChild, isRetired, peopleSorted, skillOf } from '../core/queries';
import type { GameState, JobId, Person, PersonId, ReadContext, Resource } from '../core/types';
import { canWork } from '../phases/shared/eligibility';
import { num } from '../phases/shared/value';
import { carry, seasonAt } from './steward/model';

interface Crew {
  cfg: Config;
  state: GameState;
  /** The season about to be played. */
  ctx: ReadContext;
  /** A read context for each season of the coming year (k = 0 is `ctx`). */
  year: ReadContext[];
  people: Person[];
  jobs: Map<PersonId, JobId>;
  /** Able adults below the retirement age not yet placed, in id order. */
  free: Person[];
  /** Retirees: they take only the posts an elder may volunteer for, and only with the skill. */
  volunteers: Person[];
  /** Share of the workers expected to hold a tool (Section 10), for the tool factor. */
  toolShare: number;
  /** Skilled workers kept for a specialist post: other jobs take them only when nobody else can. */
  reserved: Map<PersonId, JobId>;
  rates: Map<string, number>;
}

/** Posts held for the one person with the skill (Section 12 healer, Section 10 toolmaker). */
const SPECIALISTS: readonly JobId[] = ['healer', 'toolmaker'];

/**
 * Output of a worker in a job in season k of the year. Nobody holds a tool before the first
 * season is played, so the job's tool factor is applied for the share of workers the tools on
 * hand will equip (Section 10; M7: a founding crew arrives with a tool each).
 */
function rate(c: Crew, p: Person, job: JobId, k: number): number {
  const key = `${k}|${job}|${p.id}`;
  let value = c.rates.get(key);
  if (value === undefined) {
    value = num(c.year[k] ?? c.ctx, { kind: 'output', personId: p.id, jobId: job });
    const factor = c.ctx.registry.jobs[job].toolFactor;
    if (factor !== null) value *= 1 + (factor - 1) * c.toolShare;
    c.rates.set(key, value);
  }
  return value;
}

/** A worker's output over the whole year (the gatherers compare foraging and hunting by it). */
function yearly(c: Crew, p: Person, job: JobId): number {
  let total = 0;
  for (let k = 0; k < c.year.length; k++) total += rate(c, p, job, k);
  return total;
}

/** Everyone's need for `resource` in each season of the year, times `margin`. */
function yearNeed(c: Crew, resource: 'water' | 'warmth', margin: number): number[] {
  return c.year.map((ctx) => {
    let total = 0;
    for (const p of c.people) total += num(ctx, { kind: 'demand', personId: p.id, resource });
    return total * margin;
  });
}

function holders(c: Crew, job: JobId): Person[] {
  return c.people.filter((p) => c.jobs.get(p.id) === job);
}

/** The person's best skill outside `job`: among equals, the least specialised takes common work. */
function specialism(p: Person, job: JobId): number {
  let best = 0;
  for (const [skill, level] of Object.entries(p.skills))
    if (skill !== job) best = Math.max(best, level);
  return best;
}

function ranked(c: Crew, pool: readonly Person[], job: JobId): Person[] {
  const kept = (p: Person): number => {
    const post = c.reserved.get(p.id);
    return post !== undefined && post !== job ? 1 : 0;
  };
  return pool
    .filter((p) => canWork(c.ctx, p, job))
    .sort(
      (a, b) =>
        kept(a) - kept(b) ||
        skillOf(b, job) - skillOf(a, job) ||
        specialism(a, job) - specialism(b, job) ||
        b.strength - a.strength ||
        (a.id < b.id ? -1 : 1),
    );
}

function place(c: Crew, p: Person, job: JobId): void {
  c.jobs.set(p.id, job);
  for (const list of [c.free, c.volunteers]) {
    const i = list.indexOf(p);
    if (i >= 0) list.splice(i, 1);
  }
}

/** Place the best `count` of `pool` in `job`; returns how many were placed. */
function take(c: Crew, job: JobId, count: number, pool: readonly Person[] = c.free): number {
  let placed = 0;
  for (const p of ranked(c, pool, job)) {
    if (placed >= count) break;
    place(c, p, job);
    placed++;
  }
  return placed;
}

/** Add the best free worker to `job` until `covered()` holds or nobody can take it. */
function takeUntil(c: Crew, job: JobId, covered: () => boolean): void {
  while (!covered()) if (take(c, job, 1) === 0) return;
}

function produced(c: Crew, job: JobId, k: number): number {
  let total = 0;
  for (const p of holders(c, job)) total += rate(c, p, job, k);
  return total;
}

/**
 * Nothing short over the coming year when `job` supplies `resource` against `need` (by season),
 * starting from the stock on hand (Section 7: capacity, spoilage, 3x over capacity), and the
 * year's output covers the year's need on its own, so the second year is covered like the first.
 */
function yearCovered(c: Crew, job: JobId, resource: Resource, need: readonly number[]): boolean {
  const spoil = num(c.ctx, { kind: 'spoilage', resource });
  const { capacity } = c.state.stocks[resource];
  let stock = c.state.stocks[resource].amount;
  let made = 0;
  let wanted = 0;
  for (let k = 0; k < need.length; k++) {
    const out = produced(c, job, k);
    const want = need[k] ?? 0;
    const step = carry(stock, out, want, capacity, spoil, c.cfg.resources.overCapacitySpoilageMul);
    if (step.short > 1e-9) return false;
    stock = step.next;
    made += out;
    wanted += want;
  }
  return made >= wanted - 1e-9;
}

function farmers(c: Crew): void {
  const cfg = c.cfg;
  let plots = 0;
  for (const b of Object.values(c.state.buildings))
    if (b.condition > 0) plots += c.ctx.registry.buildings[b.type].plots;
  const seed = c.state.seedReserved + c.state.stocks.food.amount;
  const sown = Math.min(plots, Math.floor(seed / cfg.jobs.farmer.seedPerPlot + 1e-9));
  take(c, 'farmer', Math.ceil(sown / cfg.jobs.farmer.plotsPerFarmer));
}

function water(c: Crew): void {
  const need = yearNeed(c, 'water', c.cfg.newGame.crew.waterMargin);
  takeUntil(c, 'water-carrier', () => yearCovered(c, 'water-carrier', 'water', need));
}

/** Firewood for the year's warmth (Section 6: Autumn and Winter) with the margin, which also
 * covers the toolmaker's wood and the first repairs. */
function wood(c: Crew): void {
  const need = yearNeed(c, 'warmth', c.cfg.newGame.crew.woodMargin);
  takeUntil(c, 'woodcutter', () => yearCovered(c, 'woodcutter', 'wood', need));
}

/** The best holder for a skilled post among those with the skill (retirees may volunteer). */
function specialistFor(c: Crew, job: JobId): Person | undefined {
  const w = c.cfg.newGame.crew;
  const skilled = (p: Person): boolean =>
    skillOf(p, job) >= w.specialistMinSkill && !c.reserved.has(p.id);
  const volunteers = w.volunteerJobs.includes(job) ? c.volunteers.filter(skilled) : [];
  // Among equals a retiree takes the post, leaving the working adult to production.
  return [...ranked(c, volunteers, job), ...ranked(c, c.free.filter(skilled), job)].sort(
    (a, b) => skillOf(b, job) - skillOf(a, job),
  )[0];
}

/** Keep the healer and the toolmaker out of the common work before it is shared out. */
function reserveSpecialists(c: Crew): void {
  for (const job of SPECIALISTS) {
    const p = specialistFor(c, job);
    if (p !== undefined) c.reserved.set(p.id, job);
  }
}

/** One holder of a skilled post when someone has the skill: the reserved one if still free. */
function specialist(c: Crew, job: JobId): void {
  const kept = [...c.free, ...c.volunteers].find((p) => c.reserved.get(p.id) === job);
  const p = kept ?? specialistFor(c, job);
  if (p !== undefined) place(c, p, job);
}

/** Caretakers for the children under the caretaker age (Section 8: 5 each), once there are
 * enough of them; a retiree with the skill is asked first (Section 15). */
function caretakers(c: Crew): void {
  const cfg = c.cfg;
  const w = cfg.newGame.crew;
  const limit = cfg.lifecycle.caretakerUnderYears * cfg.people.seasonsPerYear;
  const small = c.people.filter((p) => p.ageSeasons < limit).length;
  if (small < w.caretakerMinChildren) return;
  const want = Math.ceil(small / cfg.jobs.caretaker.covers);
  const volunteers = c.volunteers.filter((p) => skillOf(p, 'caretaker') >= w.specialistMinSkill);
  const placed = take(c, 'caretaker', want, volunteers);
  take(c, 'caretaker', want - placed);
}

function builders(c: Crew): void {
  const w = c.cfg.newGame.crew;
  const pop = c.people.length;
  take(c, 'builder', Math.min(w.maxBuilders, Math.max(1, Math.floor(pop / w.peoplePerBuilder))));
}

/** The share of their own yield the next forager gathers (Section 8: the nth, 0.9^(n-1)). */
function forageShare(c: Crew): number {
  return c.cfg.jobs.forager.diminishing ** holders(c, 'forager').length;
}

/**
 * Everyone left gathers food. While foragers are under their cap and hunters under their share,
 * each worker goes where the year's food is larger (the next forager's diminished yield against
 * the best hunter's); then more foragers while the next still gathers a useful share of their
 * own yield. Past that every useful post is covered and the rest rest.
 */
function gatherers(c: Crew): void {
  const w = c.cfg.newGame.crew;
  const hunterCap = Math.max(1, Math.floor(c.people.length * w.maxHunterShare));
  for (;;) {
    const forager =
      holders(c, 'forager').length < w.maxForagers ? ranked(c, c.free, 'forager')[0] : undefined;
    const hunter =
      holders(c, 'hunter').length < hunterCap ? ranked(c, c.free, 'hunter')[0] : undefined;
    if (forager === undefined && hunter === undefined) break;
    const forage = forager === undefined ? -1 : yearly(c, forager, 'forager') * forageShare(c);
    const hunt = hunter === undefined ? -1 : yearly(c, hunter, 'hunter');
    if (hunter !== undefined && hunt > forage) place(c, hunter, 'hunter');
    else if (forager !== undefined) place(c, forager, 'forager');
  }
  for (;;) {
    const p = ranked(c, c.free, 'forager')[0];
    if (p === undefined || forageShare(c) < w.minForagerYieldShare) return;
    place(c, p, 'forager');
  }
}

/** Whoever is still free guards, up to the Section 6 safety need (one point per 15 people). */
function guards(c: Crew): void {
  take(c, 'guard', Math.ceil(c.people.length / c.cfg.threats.peoplePerSafetyPoint));
}

function crewOf(ctx: ReadContext, state: GameState): Crew {
  const cfg = ctx.cfg;
  const people = peopleSorted(state);
  const year = cfg.seasons.order.map((_, k) =>
    k === 0 ? ctx : { ...ctx, season: seasonAt(cfg, ctx.season, k) },
  );
  const free: Person[] = [];
  const volunteers: Person[] = [];
  for (const p of people) {
    if (isChild(cfg, p)) continue;
    if (isRetired(state, p, cfg)) volunteers.push(p);
    else free.push(p);
  }
  const tools = state.stocks.tools.amount * state.toolDurability;
  return {
    cfg,
    state,
    ctx,
    year,
    people,
    jobs: new Map(),
    free,
    volunteers,
    toolShare: Math.min(1, tools / Math.max(1, free.length)),
    reserved: new Map(),
    rates: new Map(),
  };
}

/** P-01: the founding crew, person by person (people not listed rest). */
export function foundingCrew(ctx: ReadContext, state: GameState): Map<PersonId, JobId> {
  if (ctx.cfg.newGame.crewPlan === 'm1') return m1Crew(ctx, state);
  const c = crewOf(ctx, state);
  reserveSpecialists(c);
  farmers(c);
  water(c);
  wood(c);
  specialist(c, 'healer');
  specialist(c, 'toolmaker');
  caretakers(c);
  builders(c);
  gatherers(c);
  guards(c);
  return c.jobs;
}

// ---- The M1 default (D-23, D-28, M7-08), kept for the M2a rationing fixture (M7-14).

function m1Crew(ctx: ReadContext, state: GameState): Map<PersonId, JobId> {
  const cfg = ctx.cfg;
  const pop = Object.keys(state.people).length;
  let plots = 0;
  for (const b of Object.values(state.buildings)) plots += ctx.registry.buildings[b.type].plots;
  // Heavy jobs first: elders may carry water but not cut wood.
  const counts: [JobId, number][] = [
    ['farmer', Math.ceil(plots / cfg.jobs.farmer.plotsPerFarmer)],
    ['woodcutter', Math.ceil(pop / cfg.newGame.peoplePerWoodcutter)],
    [
      'water-carrier',
      Math.ceil((pop * cfg.needs.waterPerPerson) / cfg.newGame.waterPerCarrierEstimate),
    ],
    ['forager', cfg.newGame.startingForagers],
  ];
  const jobs = new Map<PersonId, JobId>();
  const free = peopleSorted(state);
  for (const [job, count] of counts) {
    for (let i = 0; i < count; i++) {
      const candidates = free.filter((p) => canWork(ctx, p, job));
      if (candidates.length === 0) break;
      const best = candidates.reduce((a, b) => (skillOf(b, job) > skillOf(a, job) ? b : a));
      jobs.set(best.id, job);
      free.splice(free.indexOf(best), 1);
    }
  }
  return jobs;
}
